import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, ShieldOff, Plus, PauseCircle, PlayCircle } from "lucide-react";
import { storeApi, storeScopeApi } from "../../services/storeApi";
import { formatApiErrorDetail } from "../../lib/api";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { StatusBadge } from "../../components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useLocalization } from "../../hooks/useLocalization";

export default function StoreAccessPage() {
  const { t } = useLocalization();
  const perm = usePermissions();
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignTarget, setAssignTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [addStoreOpen, setAddStoreOpen] = useState(false);

  const SCOPE_LABELS = {
    ORGANIZATION_ALL: t("mgmtStoreAccess.scopeOrgWide"),
    STORE_SCOPED: t("mgmtStoreAccess.scopeSelectedStores"),
    NONE: t("mgmtStoreAccess.scopeNone"),
  };

  const load = async () => {
    setLoading(true);
    try {
      const [summaryRes, storesRes] = await Promise.all([storeScopeApi.summary(), storeApi.list(true)]);
      setRows(summaryRes.data);
      setStores(storesRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("mgmtStoreAccess.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openHistory = async (row) => {
    setHistoryTarget(row);
    const res = await storeScopeApi.get(row.user_id);
    setHistory(res.data.history);
  };

  const toggleStoreStatus = async (store) => {
    try {
      if (store.status === "ACTIVE") await storeApi.deactivate(store.id);
      else await storeApi.reactivate(store.id);
      toast.success(t("mgmtStoreAccess.storeStatusUpdated"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="store-access-page">
      <PageHelpBanner pageKey="store-access" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtStoreAccess.help")}</p>
      </PageHelpBanner>

      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="stores-management-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-800">{t("mgmtStoreAccess.storesHeading")}</h3>
          {perm.canManageStoreAccess && (
            <Button size="sm" data-testid="add-store-btn" onClick={() => setAddStoreOpen(true)}>
              <Plus size={14} className="mr-1.5" /> {t("mgmtStoreAccess.addStore")}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="stores-list">
          {stores.map((s) => (
            <div key={s.id} data-testid={`store-chip-${s.id}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${s.status === "ACTIVE" ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-gray-100 opacity-60"}`}>
              <span className="font-medium text-gray-800">{s.display_name || s.name}</span>
              <StatusBadge status={s.status} />
              {perm.canManageStoreAccess && (
                <button
                  type="button"
                  data-testid={`store-toggle-status-${s.id}`}
                  onClick={() => toggleStoreStatus(s)}
                  title={s.status === "ACTIVE" ? t("mgmtStoreAccess.deactivateStore") : t("mgmtStoreAccess.reactivateStore")}
                  className="text-gray-400 hover:text-gray-700"
                >
                  {s.status === "ACTIVE" ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
                </button>
              )}
            </div>
          ))}
          {stores.length === 0 && <p className="text-sm text-gray-400">{t("mgmtStoreAccess.noStoresYet")}</p>}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtStoreAccess.title")}</h2>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtStoreAccess.manager")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtAccessProfiles.title")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtStoreAccess.scope")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtStoreAccess.assignedStores")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtStoreAccess.lastChanged")}</th>
              <th className="px-4 py-3 font-medium text-right">{t("mgmtMedical.colActions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">{t("common.loading")}</td></tr>}
            {!loading && rows.map((row) => (
              <tr key={row.user_id} data-testid={`store-access-row-${row.user_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{row.name}</div>
                  <div className="text-xs text-gray-500">{row.email}</div>
                </td>
                <td className="px-4 py-3 text-gray-600">{t(`accessProfileLabel.${row.role}`)}</td>
                <td className="px-4 py-3">
                  {row.organization_scope === "NONE" ? (
                    <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium" data-testid={`store-access-none-${row.user_id}`}>
                      <ShieldOff size={13} /> {t("mgmtStoreAccess.scopeNone")}
                    </span>
                  ) : (
                    <StatusBadge status={row.organization_scope} />
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {row.organization_scope === "ORGANIZATION_ALL" ? t("mgmtStoreAccess.allStores") : (row.store_names?.join(", ") || "-")}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {row.scope_last_changed_at ? new Date(row.scope_last_changed_at).toLocaleString() : t("mgmtLeaveDetail.never")}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button data-testid={`store-access-history-btn-${row.user_id}`} onClick={() => openHistory(row)} className="text-xs font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                    <History size={13} /> {t("mgmtStoreAccess.history")}
                  </button>
                  {perm.canManageStoreAccess && (
                    <button data-testid={`store-access-assign-btn-${row.user_id}`} onClick={() => setAssignTarget(row)} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                      {t("mgmtStoreAccess.assignStores")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">{t("mgmtStoreAccess.noManagementAccounts")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {addStoreOpen && (
        <AddStoreDialog onClose={() => setAddStoreOpen(false)} onSaved={() => { setAddStoreOpen(false); load(); }} t={t} />
      )}

      {assignTarget && (
        <AssignScopeDialog
          target={assignTarget}
          stores={stores}
          canGrantOrgWide={perm.canGrantOrgWideScope}
          onClose={() => setAssignTarget(null)}
          onSaved={() => { setAssignTarget(null); load(); }}
          t={t}
        />
      )}

      <Dialog open={!!historyTarget} onOpenChange={(open) => !open && setHistoryTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="store-access-history-dialog">
          <DialogHeader><DialogTitle>{t("mgmtStoreAccess.accessHistory")} - {historyTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="border border-gray-100 rounded-lg p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{SCOPE_LABELS[h.organization_scope]}</span>
                  <span className="text-gray-500">{new Date(h.effective_from).toLocaleDateString()}{h.effective_to ? ` - ${new Date(h.effective_to).toLocaleDateString()}` : ` (${t("mgmtPayroll.current")})`}</span>
                </div>
                <p className="text-gray-500 mt-1">{h.reason}</p>
              </div>
            ))}
            {history.length === 0 && <p className="text-sm text-gray-500">{t("mgmtStoreAccess.noHistoryYet")}</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignScopeDialog({ target, stores, canGrantOrgWide, onClose, onSaved, t }) {
  const [orgScope, setOrgScope] = useState(target.organization_scope || "NONE");
  const [storeIds, setStoreIds] = useState(new Set(target.store_ids || []));
  const [reason, setReason] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleStore = (id) => {
    setStoreIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedStoreNames = stores.filter((s) => storeIds.has(s.id)).map((s) => s.display_name || s.name);

  const handleSave = async () => {
    setError("");
    if (!reason.trim()) { setError(t("mgmtStoreAccess.reasonRequiredAccessChange")); return; }
    setSaving(true);
    try {
      await storeScopeApi.assign(target.user_id, {
        organization_scope: orgScope,
        store_ids: orgScope === "STORE_SCOPED" ? Array.from(storeIds) : [],
        reason,
        effective_from: effectiveFrom ? new Date(effectiveFrom).toISOString() : null,
        effective_to: effectiveTo ? new Date(effectiveTo).toISOString() : null,
      });
      toast.success(t("mgmtStoreAccess.accessUpdated"));
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="store-access-assign-dialog">
        <DialogHeader><DialogTitle>{t("mgmtStoreAccess.assignStoreAccess")} - {target.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAccess.scopeType")}</label>
            <select
              data-testid="store-access-scope-type-select"
              value={orgScope}
              onChange={(e) => setOrgScope(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm"
            >
              <option value="NONE">{t("mgmtStoreAccess.scopeNone")}</option>
              <option value="STORE_SCOPED">{t("mgmtStoreAccess.scopeSelectedStores")}</option>
              {canGrantOrgWide && <option value="ORGANIZATION_ALL">{t("mgmtStoreAccess.scopeOrgWide")}</option>}
            </select>
          </div>

          {orgScope === "STORE_SCOPED" && (
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAccess.stores")}</label>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto" data-testid="store-access-store-checklist">
                {stores.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer">
                    <Checkbox checked={storeIds.has(s.id)} onCheckedChange={() => toggleStore(s.id)} data-testid={`store-access-store-checkbox-${s.id}`} />
                    {s.display_name || s.name} {s.status !== "ACTIVE" && <span className="text-xs text-gray-500">({t("mgmtStoreAccess.inactive")})</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAccess.effectiveFromOptional")}</label>
              <input type="date" data-testid="store-access-effective-from-input" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAccess.expiresOptional")}</label>
              <input type="date" data-testid="store-access-effective-to-input" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600" data-testid="store-access-effective-summary">
            <span className="font-semibold">{t("mgmtStoreAccess.effectiveAccess")}: </span>
            {orgScope === "ORGANIZATION_ALL" && t("mgmtStoreAccess.fullOrgAccess")}
            {orgScope === "NONE" && t("mgmtStoreAccess.noStoreDataVisible")}
            {orgScope === "STORE_SCOPED" && (selectedStoreNames.length ? `${t("mgmtStoreAccess.limitedTo")}: ${selectedStoreNames.join(", ")}.` : t("mgmtStoreAccess.selectAtLeastOneStore"))}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAssignment.reasonRequiredForAudit")}</label>
            <textarea data-testid="store-access-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          </div>

          {error && <p className="text-sm text-red-500" data-testid="store-access-error">{error}</p>}
        </div>
        <DialogFooter>
          <button data-testid="store-access-cancel-btn" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold border border-gray-300">{t("common.cancel")}</button>
          <button
            data-testid="store-access-save-btn"
            onClick={handleSave}
            disabled={saving || (orgScope === "STORE_SCOPED" && storeIds.size === 0)}
            className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40"
          >
            {saving ? t("mgmtStoreAssignment.savingEllipsis") : t("common.save")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddStoreDialog({ onClose, onSaved, t }) {
  const [name, setName] = useState("");
  const [japaneseName, setJapaneseName] = useState("");
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setError("");
    if (!name.trim()) { setError(t("mgmtStoreAccess.storeNameRequired")); return; }
    setSaving(true);
    try {
      await storeApi.create({ name: name.trim(), japanese_name: japaneseName.trim() || null, location: location.trim() || null });
      toast.success(t("mgmtStoreAccess.storeCreated"));
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-store-dialog">
        <DialogHeader><DialogTitle>{t("mgmtStoreAccess.addStore")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAccess.storeName")}</label>
            <Input data-testid="add-store-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("mgmtStoreAccess.storeNamePlaceholder")} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAccess.storeJapaneseNameOptional")}</label>
            <Input data-testid="add-store-japanese-name-input" value={japaneseName} onChange={(e) => setJapaneseName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAccess.storeLocationOptional")}</label>
            <Input data-testid="add-store-location-input" value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-500" data-testid="add-store-error">{error}</p>}
        </div>
        <DialogFooter>
          <button data-testid="add-store-cancel-btn" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold border border-gray-300">{t("common.cancel")}</button>
          <button
            data-testid="add-store-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40"
          >
            {saving ? t("mgmtStoreAssignment.savingEllipsis") : t("common.save")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
